import { useState, useEffect, useCallback } from "react";
import { AWARDS_API, fetchWithAuth } from "../utils/api";
import { scoringRulesService } from "../services/scoringRulesService";

/**
 * Hook for managing scoring rules settings
 * Separate from awards settings for better organization
 */
export function useRulesSettings() {
  const [scoringRules, setScoringRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isExistingConfig, setIsExistingConfig] = useState(false);

  /**
   * Fetch current rules configuration
   */
  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the full awards configuration to extract scoring rules
      const data = await fetchWithAuth(AWARDS_API);
      
      // Check if we have existing scoring rules
      const existingRules = data.scoringRules || {};
      const hasExistingData = Object.keys(existingRules).length > 0;
      
      setIsExistingConfig(hasExistingData);
      setScoringRules(existingRules);
      setHasChanges(false);
      
      console.log('Fetched scoring rules:', existingRules);
    } catch (err) {
      setError(err.message || "Failed to load scoring rules");
      console.error("Error loading scoring rules:", err);
      setIsExistingConfig(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save scoring rules configuration
   */
  const saveRules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      // First, get the current awards configuration
      const currentConfig = await fetchWithAuth(AWARDS_API);
      
      // Use POST for new configurations, PUT for updates
      const method = isExistingConfig ? "PUT" : "POST";
      const actionText = isExistingConfig ? "updated" : "created";
      
      console.log(`Using ${method} method for scoring rules (existing: ${isExistingConfig})`);
      
      // Merge scoring rules with existing awards configuration
      const updatedConfig = {
        ...currentConfig,
        scoringRules
      };
      
      const response = await fetchWithAuth(AWARDS_API, {
        method: method,
        body: JSON.stringify(updatedConfig)
      });
      
      // Update local state with the saved data
      if (response && response.scoringRules) {
        setScoringRules(response.scoringRules);
        setIsExistingConfig(true);
      }
      
      setHasChanges(false);
      setSuccessMessage(`Scoring rules ${actionText} successfully!`);
      
      // Clear scoring rules cache so competitions get updated rules
      scoringRulesService.clearCache();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
      return true;
    } catch (err) {
      setError(err.message || "Failed to save scoring rules");
      console.error("Error saving scoring rules:", err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [scoringRules, isExistingConfig]);

  /**
   * Update scoring rules and mark as changed
   */
  const updateScoringRules = useCallback((rules) => {
    setScoringRules(rules);
    setHasChanges(true);
    setError(null);
    setSuccessMessage(null);
  }, []);

  /**
   * Reset rules to last saved state
   */
  const resetRules = useCallback(() => {
    fetchRules();
  }, [fetchRules]);

  /**
   * Check if rules are valid
   */
  const validateRules = useCallback((rules = scoringRules) => {
    return scoringRulesService.validateScoringRules(rules);
  }, [scoringRules]);

  // Load rules on mount
  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchRulesWithAbort = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch from the AWARDS_API endpoint that stores scoring rules
        const data = await fetchWithAuth(AWARDS_API, { signal: abortController.signal });
        
        // Only update state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          console.log('Fetched scoring rules:', data.scoringRules);
          
          // Use the scoring rules from the response, or defaults if none exist
          const rules = data.scoringRules || {};
          setScoringRules(rules);
          setHasChanges(false);
        }
      } catch (err) {
        // Only set error if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setError(err.message || "Failed to load scoring rules");
          console.error("Error loading scoring rules:", err);
          // Use defaults on error
          setScoringRules({});
        }
      } finally {
        // Only update loading state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchRulesWithAbort();
    
    // Cleanup function to abort the request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, []); // Remove fetchRules dependency to prevent unnecessary re-runs

  // Clear success message when rules change
  useEffect(() => {
    if (hasChanges && successMessage) {
      setSuccessMessage(null);
    }
  }, [hasChanges, successMessage]);

  return {
    // State
    scoringRules,
    loading,
    error,
    hasChanges,
    successMessage,
    isExistingConfig,
    
    // Actions
    updateScoringRules,
    saveRules,
    resetRules,
    fetchRules,
    validateRules
  };
}
